import { memo, useMemo } from 'react';
import { Box, Stack, Button } from '@mui/material';
import { IInspectionIpsativesProps } from 'interface/project/projectInfo';
import { Detail3PropsType, ICmnInspectionProps, ToggleValue } from 'interface/inspection/InspectionInfo';
import DocumentCard from 'components/inspection/player/DocumentCard';
import DocumentCard2 from 'components/inspection/player/DocumentCard2';
import ProgressIndicator from 'components/inspection/player/ProgressIndicator';
import NavigationButtons from 'components/inspection/player/NavigationButtons';
import { usePlayer } from 'hooks/usePlayer';
import useInspection from 'hooks/useInspection';
import DocumentCard3 from './DocumentCard3';

interface IPlayerLeftCmnProps {
  isMobile: boolean;
  inspectionData: IInspectionIpsativesProps[];
  result: ICmnInspectionProps[];
  type?: 'NN' | 'N' | 'A' | 'AA' | Detail3PropsType;
  version: 'left1' | 'left2' | 'left3';
  total?: number;
}
function PlayerLeftCmn({
  isMobile,
  inspectionData,
  result,
  type,
  version,
  total = 0,
}: IPlayerLeftCmnProps): JSX.Element {
  const {
    progressInfo,
    buttonInfo,
    handlePrev,
    handleNext,
    isNextDisabled,
    isPrevDisabled,
    ipsatives,
    AAInfo,
    AInfo,
    NNInfo,
    NInfo,
    NNTempNNInfo,
    NNTempNInfo,
    NNTempAInfo,
    NNTempAAInfo,
    NTempNNInfo,
    NTempNInfo,
    NTempAInfo,
    NTempAAInfo,
    ATempNNInfo,
    ATempNInfo,
    ATempAInfo,
    ATempAAInfo,
    AATempNNInfo,
    AATempNInfo,
    AATempAInfo,
    AATempAAInfo,
    detailProgressInfo,
  } = usePlayer(inspectionData || [], result);

  const { 
    updateInspectionFirstClassificationItem, 
    updateInspectionSecondClassificationItem, 
    updateInspectionThirdClassificationItem,
    inspectionFirstClassification,
    inspectionSecondClassification,
    inspectionThirdClassification,
    objectKeyIndex,
  } = useInspection();

  const handleAutoFill = () => {
    if (!inspectionData || inspectionData.length === 0) return;

    const totalCount = inspectionData.length;
    const quarter = Math.floor(totalCount / 4);

    console.log(`자동 채우기 시작: 총 ${totalCount}개, 1/4 지점: ${quarter}`);

    if (version === 'left1') {
      // 1단계 분류
      inspectionData.forEach((item, index) => {
        let value: ToggleValue = 'NN';

        if (index < quarter) {
          value = 'NN';
        } else if (index < quarter * 2) {
          value = 'N';
        } else if (index < quarter * 3) {
          value = 'A';
        } else {
          value = 'AA';
        }

        const resultObj: ICmnInspectionProps = {
          ...item,
          sort: index,
          value,
          parent: value, // 1단계에서는 value를 parent로 사용
          type: value,
        };

        // inspectionFirstClassification에 저장
        updateInspectionFirstClassificationItem(value as 'NN' | 'N' | 'A' | 'AA', resultObj);
      });
    } else if (version === 'left2' && type) {
      // 2단계 분류
      const parentValue = type as 'NN' | 'N' | 'A' | 'AA';
      
      // 부모 정보를 inspectionFirstClassification에서 가져옴
      const parentGroupKey = `${parentValue}Temp` as 'NNTemp' | 'NTemp' | 'ATemp' | 'AATemp';
      const parentGroup = inspectionFirstClassification[parentGroupKey];
      
      inspectionData.forEach((item, index) => {
        let value: ToggleValue = 'NN';

        if (index < quarter) {
          value = 'NN';
        } else if (index < quarter * 2) {
          value = 'N';
        } else if (index < quarter * 3) {
          value = 'A';
        } else {
          value = 'AA';
        }

        // 부모 정보 찾기
        const parentInfo = parentGroup.find(p => p.id === item.id);
        const typeKey = `${parentValue}Temp${value}` as keyof import('interface/inspection/InspectionInfo').IInspectionSecondClassificationProps;

        const resultObj: ICmnInspectionProps = {
          ...item,
          sort: parentInfo?.sort ?? index,
          value,
          type: typeKey,
          parent: parentValue,
        };

        // inspectionSecondClassification에 저장
        updateInspectionSecondClassificationItem(typeKey, resultObj);
      });
    } else if (version === 'left3' && type) {
      // 3단계 분류
      // 현재 그룹 키 찾기 (objectKeyIndex 사용)
      const keys = Object.keys(inspectionThirdClassification);
      const parentKeys = new Set<string>();
      keys.forEach(key => {
        const parentKey = key.replace(/\d+$/, '');
        parentKeys.add(parentKey);
      });
      const parentKeysArray = Array.from(parentKeys);
      const currentGroupKey = parentKeysArray[objectKeyIndex] ?? '';
      
      if (!currentGroupKey) return;
      
      // 부모 정보를 inspectionSecondClassification에서 가져옴
      const parentGroup = inspectionSecondClassification[currentGroupKey as keyof typeof inspectionSecondClassification] || [];
      
      const getSelectionValue = (selection: string): number => {
        const mapping: Record<string, number> = {
          NN: 1,
          N: 2,
          A: 3,
          AA: 4,
        };
        return mapping[selection] ?? 0;
      };
      
      inspectionData.forEach((item, index) => {
        let value: ToggleValue = 'NN';

        if (index < quarter) {
          value = 'NN';
        } else if (index < quarter * 2) {
          value = 'N';
        } else if (index < quarter * 3) {
          value = 'A';
        } else {
          value = 'AA';
        }

        const parentInfo = parentGroup.find(p => p.id === item.id);
        const typeKey = `${currentGroupKey}${getSelectionValue(value as string)}`;
        
        const resultObj: ICmnInspectionProps = {
          ...item,
          sort: parentInfo?.sort ?? index,
          value,
          type: typeKey,
          parent: currentGroupKey,
        };

        // 기존 타입 찾기 (이미 분류된 경우)
        const existingType = Object.keys(inspectionThirdClassification).find(key => {
          const arr = inspectionThirdClassification[key];
          return arr?.some(i => i.id === item.id);
        });

        // inspectionThirdClassification에 저장
        updateInspectionThirdClassificationItem(typeKey, resultObj, existingType);
      });
    }
  };

  const handleAutoFill115 = () => {
    if (!inspectionData || inspectionData.length === 0) return;

    const totalCount = inspectionData.length;
    // 1:0:0:9 비율 계산 (총 10)
    const ratioNN = Math.floor(totalCount / 10); // 1/10
    const ratioN = 0; // 0
    const ratioA = 0; // 0
    const ratioAA = totalCount - ratioNN; // 나머지는 AA (9/10)

    console.log(`자동 채우기 시작 (1:0:0:9): 총 ${totalCount}개, NN:${ratioNN}, N:${ratioN}, A:${ratioA}, AA:${ratioAA}`);

    if (version === 'left1') {
      // 1단계 분류
      inspectionData.forEach((item, index) => {
        let value: ToggleValue = 'NN';

        if (index < ratioNN) {
          value = 'NN';
        } else {
          value = 'AA';
        }

        const resultObj: ICmnInspectionProps = {
          ...item,
          sort: index,
          value,
          parent: value, // 1단계에서는 value를 parent로 사용
          type: value,
        };

        // inspectionFirstClassification에 저장
        updateInspectionFirstClassificationItem(value as 'NN' | 'N' | 'A' | 'AA', resultObj);
      });
    } else if (version === 'left2' && type) {
      // 2단계 분류
      const parentValue = type as 'NN' | 'N' | 'A' | 'AA';
      
      // 부모 정보를 inspectionFirstClassification에서 가져옴
      const parentGroupKey = `${parentValue}Temp` as 'NNTemp' | 'NTemp' | 'ATemp' | 'AATemp';
      const parentGroup = inspectionFirstClassification[parentGroupKey];
      
      inspectionData.forEach((item, index) => {
        let value: ToggleValue = 'NN';

        if (index < ratioNN) {
          value = 'NN';
        } else {
          value = 'AA';
        }

        // 부모 정보 찾기
        const parentInfo = parentGroup.find(p => p.id === item.id);
        const typeKey = `${parentValue}Temp${value}` as keyof import('interface/inspection/InspectionInfo').IInspectionSecondClassificationProps;

        const resultObj: ICmnInspectionProps = {
          ...item,
          sort: parentInfo?.sort ?? index,
          value,
          type: typeKey,
          parent: parentValue,
        };

        // inspectionSecondClassification에 저장
        updateInspectionSecondClassificationItem(typeKey, resultObj);
      });
    } else if (version === 'left3' && type) {
      // 3단계 분류
      // 현재 그룹 키 찾기 (objectKeyIndex 사용)
      const keys = Object.keys(inspectionThirdClassification);
      const parentKeys = new Set<string>();
      keys.forEach(key => {
        const parentKey = key.replace(/\d+$/, '');
        parentKeys.add(parentKey);
      });
      const parentKeysArray = Array.from(parentKeys);
      const currentGroupKey = parentKeysArray[objectKeyIndex] ?? '';
      
      if (!currentGroupKey) return;
      
      // 부모 정보를 inspectionSecondClassification에서 가져옴
      const parentGroup = inspectionSecondClassification[currentGroupKey as keyof typeof inspectionSecondClassification] || [];
      
      const getSelectionValue = (selection: string): number => {
        const mapping: Record<string, number> = {
          NN: 1,
          N: 2,
          A: 3,
          AA: 4,
        };
        return mapping[selection] ?? 0;
      };
      
      inspectionData.forEach((item, index) => {
        let value: ToggleValue = 'NN';

        if (index < ratioNN) {
          value = 'NN';
        } else {
          value = 'AA';
        }

        const parentInfo = parentGroup.find(p => p.id === item.id);
        const typeKey = `${currentGroupKey}${getSelectionValue(value as string)}`;
        
        const resultObj: ICmnInspectionProps = {
          ...item,
          sort: parentInfo?.sort ?? index,
          value,
          type: typeKey,
          parent: currentGroupKey,
        };

        // 기존 타입 찾기 (이미 분류된 경우)
        const existingType = Object.keys(inspectionThirdClassification).find(key => {
          const arr = inspectionThirdClassification[key];
          return arr?.some(i => i.id === item.id);
        });

        // inspectionThirdClassification에 저장
        updateInspectionThirdClassificationItem(typeKey, resultObj, existingType);
      });
    }
  };

  const viewerInfo = useMemo(() => {
    if (version === 'left1') {
      return ipsatives;
    }

    if (version === 'left2') {
      const infoMap = { AA: AAInfo, A: AInfo, NN: NNInfo, N: NInfo };
      return infoMap[type as 'NN' | 'N' | 'A' | 'AA'] || [];
    }

    if (version === 'left3') {
      // 3단계 분류에서는 inspectionData를 직접 사용
      return inspectionData || [];
    }

    return [];
  }, [version, type, ipsatives, AAInfo, AInfo, NNInfo, NInfo, inspectionData]);

  const { value } = buttonInfo;
  const currentProgress = useMemo(() => {
    if (version === 'left1') return progressInfo;
    if (version === 'left2') return detailProgressInfo;
    if (version === 'left3') return detailProgressInfo;
    return 0;
  }, [detailProgressInfo, progressInfo, version]);

  const totalItems = useMemo(() => {
    if (version === 'left1') return ipsatives.length ?? 0;
    if (version === 'left2') return viewerInfo.length ?? 0;
    if (version === 'left3') return inspectionData?.length ?? 0;
    return 0;
  }, [ipsatives.length, version, viewerInfo.length, inspectionData?.length]);

  const DocumentComponent = useMemo(() => {
    if (version === 'left1')
      return (
        <DocumentCard
          inspectionIpsativesInfo={inspectionData}
          value={value}
          sort={currentProgress}
          obj={viewerInfo[currentProgress]}
        />
      );
    if (version === 'left2') {
      const currentObj = viewerInfo[currentProgress];
      if (!currentObj) {
        return null; // 또는 기본값 반환
      }
      return <DocumentCard2 value={value} obj={currentObj} />;
    }
    if (version === 'left3') {
      const currentObj = inspectionData?.[detailProgressInfo];
      if (!currentObj) {
        return null;
      }
      return <DocumentCard3 value={value} obj={currentObj} total={total} />;
    }
    return null;
  }, [currentProgress, detailProgressInfo, inspectionData, total, value, version, viewerInfo]);
  return (
    <Box
      sx={{
        flex: { xs: 1, md: 3 },
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderRadius: '12px',
        p: 0,
        height: '100%',
        boxShadow: '0px 0px 4px 0px #00000033',
      }}
    >
      <Box
        sx={{
          overflowY: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
          height: '100%',
        }}
      >
        <Stack
          direction="column"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
            height: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'white',
              width: '100%',
              // maxWidth: 580,
              borderRadius: '12px',
              boxShadow: '0px 0px 32px 0px #00000033',
              flex: 1,
            }}
          >
            {DocumentComponent}
          </Box>
          <Box sx={{ height: { xs: 10, md: 60 } }} />
          <ProgressIndicator current={currentProgress + 1} total={totalItems} />
          {isMobile && <Box sx={{ height: 5 }} />}
        </Stack>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: { xs: 45, md: 100 },
          pt: { xs: 0.5, md: 2 },
          pb: { xs: 0.5, md: 2 },
        }}
      >
        <NavigationButtons
          onPrev={handlePrev}
          onNext={handleNext}
          isNextDisabled={isNextDisabled}
          isPrevDisabled={isPrevDisabled}
        />
        {(version === 'left1' || version === 'left2' || version === 'left3') && (
          <>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={handleAutoFill}
              sx={{ position: 'absolute', right: 20, display: { xs: 'none', md: 'block' } }}
            >
              자동 1/4
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={handleAutoFill115}
              sx={{ position: 'absolute', right: 100, display: { xs: 'none', md: 'block' } }}
            >
              자동 1:0:0:9
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}

export default memo(PlayerLeftCmn);
